import { autoinject, useView } from "aurelia-framework";
import { Claims } from "../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { LookupValue } from "../../../../../models/lookups/lookup.models";
import { BusinessUnitDomainModel, BusinessUnitModel } from "../../../../../models/customers/customer.models";
import { ManageRightShipUsersClientService } from "../../../../../services/modules/righship-qi-manage-users.service";
import { ListModel } from "../../../../../models/lists/list.models";
import { ListService } from "../../../../../services/modules/list-client.service";
import { CustomerService } from "../../../../../services/modules/customer-client.service";
import { Guid } from "../../../../../utilities/guid";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../client-validation-system/custom-validation-renderer";

@autoinject
@useView('views/pages/modules/business-unit/tabs/business-unit-overview.html')
export class BusinessUnitOverview {

    canEditAuthorizedUserForAmendingVettingRules: boolean;
    canEditVetAutoCancellation: boolean;
    canModifyVettingListSettingsAtBusinessUnit: boolean;
    canEditFormAutoCancellation: boolean;
    canEditBusinessUnits: boolean;
    users: LookupValue[] = [];
    model: BusinessUnitModel;
    oldBusinessUnit: BusinessUnitModel;
    public lists: ListModel[] = [];
    public canModifyAllowedDomains: boolean = false;

    constructor(userClaimService: UserClaimsService, private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private listService:ListService, private customerService: CustomerService, 
        private validationController: ValidationController) {
            this.canEditVetAutoCancellation = userClaimService.hasPermission(Claims.CanEditVetAutoCancellation);
            this.canEditFormAutoCancellation = userClaimService.hasPermission(Claims.CanEditFormAutoCancellation);
            this.canModifyVettingListSettingsAtBusinessUnit = userClaimService.hasPermission(Claims.CanModifyVettingListSettingsAtBusinessUnit);
            this.canEditBusinessUnits = userClaimService.hasPermission(Claims.CanEditBusinessUnits);
            this.canModifyAllowedDomains = userClaimService.hasPermission(Claims.CanModifyAllowedDomains);
            this.validationController.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: BusinessUnitModel) {
        this.model = model;

        if (!this.model.numberOfDaysBeforeFormExpires) {
            this.model.numberOfDaysBeforeFormExpires = 25;
        }
            
        if (!this.model.numberOfDaysBeforeVetExpires) {
            this.model.numberOfDaysBeforeVetExpires = 25;
        }

        if (model.businessUnitId) {
            this.manageRightShipUsersClientService.getSimplifiedUsersFromBusinessUnit(model.businessUnitId).then(x => {
                this.users = x.map(x => <LookupValue>{ id: x.id, name: x.name });

                for (const user of this.users) {
                    const u = this.model.businessUnitUsers.filter(x => x.userId == user.id)[0];
                    (<any>user).isSelected = u && u.canAmmendVetRules;
                }
            });
        } else if (model.customerId) {
            this.users = [];
        }

        this.lists = await this.listService.getBusinessUnitLists(model.businessUnitId);
        await this.loadBusinessDomains();
    }

    async loadBusinessDomains() {
        if (this.validationController) {
            this.validationController.reset();
        }

        this.oldBusinessUnit = CloneHelper.cloneObject(this.model);
        const buDomains = await this.customerService.getBusinessUnitAllowedDomains(this.model.businessUnitId);
        this.model.allowedDomains = buDomains.allowedDomains?.map(x => {
            return {
                businessUnitDomainId: this.model.businessUnitId,
                name: x,
                identifier: Guid.generateGuid()
            };
        }) || [];
        
        this.oldBusinessUnit.allowedDomains = CloneHelper.cloneObject(this.model.allowedDomains);
        this.model.allowedDomains.forEach(x => this.addDomainValidation(x));
    }

    selectedItemsChanged($event) {
        const users: LookupValue[] = $event.detail;

        for (const u of this.model.businessUnitUsers) {
            const user = users.filter(x => x.id == u.userId && (<any>x).isSelected)[0];
            u.canAmmendVetRules = user != null;
        }
    }

    removeList(index: number){
        this.model.businessUnitVettingLists.splice(index, 1);
    }

    addList(){
        this.model.businessUnitVettingLists.push({
            listId: 0
        });
    }

    addDomain() {
        this.model.allowedDomains.push({
            name: '',
            identifier: Guid.generateGuid()
        });
        this.addDomainValidation(this.model.allowedDomains[this.model.allowedDomains.length - 1]);
    }

    removeDomain(index: number) {
        this.model.allowedDomains.splice(index, 1);
    }

    hasChanges() {
        let hasChange = false;
        if(this.oldBusinessUnit && this.model) {
            const refinedCustomerDomains = [...this.model.allowedDomains].map(x => x.name.trim()).sort();
            const refinedOldCustomerDomains = [...this.oldBusinessUnit.allowedDomains].map(x => x.name.trim()).sort();

            const sameDomainElements = refinedCustomerDomains.length === refinedOldCustomerDomains.length && 
                refinedCustomerDomains.every((val, index) => val === refinedOldCustomerDomains[index]);

            if (!sameDomainElements) {
                hasChange = true;
            }
        }
        return hasChange;
    }

    addDomainValidation(domain: BusinessUnitDomainModel) {
        ValidationRules
            .ensure<BusinessUnitDomainModel, string>(x => x.name)
            .required()
            .withMessage("Domains must not be blank.")
            .on(domain);
    }

    async isValid(): Promise<boolean>
    {
        const result = await this.validationController.validate();
        return result.valid;
    }
}